import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

interface ChangeTypeData {
  normal: number;
  standard: number;
  emergency: number;
}

interface ChangeTypeBarChartProps {
  data: ChangeTypeData;
  onBarClick?: (type: string) => void;
}

const typeLabels: Record<string, string> = {
  normal: 'Normal',
  standard: 'Standard',
  emergency: 'Emergency',
};

const typeColors: Record<string, string> = {
  normal: 'rgba(59, 130, 246, 0.8)',      // blue
  standard: 'rgba(16, 185, 129, 0.8)',    // green
  emergency: 'rgba(239, 68, 68, 0.8)',    // red
};

const ChangeTypeBarChart: React.FC<ChangeTypeBarChartProps> = ({ data, onBarClick }) => {
  const typeKeys = Object.keys(typeLabels);
  const labels = typeKeys.map(key => typeLabels[key]);
  const values = typeKeys.map(key => data[key as keyof ChangeTypeData] || 0);
  const colors = typeKeys.map(key => typeColors[key]);
  const total = values.reduce((sum, val) => sum + val, 0);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Change Requests',
        data: values,
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 1,
        borderRadius: 8,
        barThickness: 60,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw as number;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${value} changes (${percentage}%)`;
          }
        }
      },
      datalabels: {
        display: true,
        color: '#fff',
        font: {
          weight: 'bold' as const,
          size: 14,
        },
        anchor: 'center' as const,
        align: 'center' as const,
        formatter: (value: number) => value,
      }
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { precision: 0 }
      }
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index;
        onBarClick(typeKeys[index]);
      }
    },
  };

  return (
    <div id="chart-change-type-bar" className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Change Request Type Distribution</h3>
        <span className="text-xs text-gray-500">Click to filter</span>
      </div>
      <div className="h-64">
        <Bar data={chartData} options={options} />
      </div>
      <div className="mt-4 flex flex-wrap gap-2 justify-center">
        {typeKeys.map((type, index) => (
          <button
            key={type}
            onClick={() => onBarClick?.(type)}
            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium hover:opacity-80 transition-opacity"
            style={{ backgroundColor: typeColors[type], color: 'white' }}
          >
            {typeLabels[type]}: {values[index]}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ChangeTypeBarChart;
